<template>
  <idf-carousel
    ref="theCarousel"
    v-if="displayedProducts.length"
    v-observe-visibility="{
      callback: carouselVisibilityChanged,
      throttle: 1000,
      intersection: { threshold: 0.75 },
      once: true,
    }"
    @items-visibility-change="itemsVisibilityChanged"
    :background="
      ((backgroundColor || backgroundImage) && { color: backgroundColor, url: backgroundImage }) || undefined
    "
    :value="{ title, description }"
    class="carousel-products"
  >
    <template v-if="logo" #header>
      <div class="carousel-products__logo">
        <adn-image :source="logo" alt="" :cloudinary-options="logoOptions" @click="onLogoClicked" />
      </div>
    </template>
    <template v-for="(product, index) in displayedProducts" #[index] :key="product._id">
      <p-product-thumbnail
        :isAuthenticated="isAuthenticated"
        :productId="product._id"
        :productSlug="product.slug"
        :horizontal-card="horizontalCard"
        fixed-width
        :display-supplier-name="displaySupplierName"
        data-test-product-thumbnail
        @click.capture="$emit('product-click', product)"
      />
    </template>
    <template v-if="buttonLabel" #footer>
      <div class="carousel-products__footer">
        <adn-button :href="buttonUrl" kind="secondary" markup="a" @click="onCtaClicked">
          {{ buttonLabel }}
        </adn-button>
      </div>
    </template>
  </idf-carousel>
</template>

<script lang="ts">
import { useStore } from 'vuex'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import IdfCarousel from '@b2ag/design-system/src/components/IdfCarousel/IdfCarousel.vue'
import { type Ref, computed, defineComponent, onMounted, ref } from 'vue'
import ObserveVisibility from 'vue3-observe-visibility/src/directives/observe-visibility'
import { useEditorialsReactions } from '@b2ag/marketing/src/application/composables/useEditorialsReactions'
import type { CooperativeProduct } from '../../services/product'
import type { FullProduct } from '../../services/shop.business'
import PProductThumbnail from '../../presenters/PProductThumbnail/PProductThumbnail.vue'
import AdnImage from '@b2ag/design-system/src/components/AdnImage/AdnImage.vue'
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'

interface IdfCarouselExpose {
  visibleItemsIdx: number[]
}

export default defineComponent({
  name: 'CCarouselProduct',
  components: { IdfCarousel, PProductThumbnail, AdnImage, AdnButton },
  directives: { ObserveVisibility },
  props: {
    logo: String,
    logoOptions: {
      type: String,
      default: 'h_44,q_auto,f_auto,c_pad',
    },
    title: String,
    description: String,
    backgroundImage: String,
    backgroundColor: String,
    products: { default: () => [], type: Array<string> },
    buttonLabel: String,
    buttonUrl: String,
    anchorLabel: String,
    horizontalCard: { type: Boolean, default: false },
    displaySupplierName: { type: Boolean, default: true },
    ctaClickEventName: {
      type: String,
      default: 'product_thumbnail_click_cta',
    },
  },
  setup(props, context) {
    const theCarousel: Ref<IdfCarouselExpose | null> = ref(null)
    let carouselDisplayed = false
    let currentDisplayed = []
    const store = useStore()
    const displayedProducts = ref<FullProduct<CooperativeProduct>[]>([])

    const isAuthenticated = computed(() => store.getters.isAuthenticated)
    const editorialsReactions = useEditorialsReactions().inject()

    onMounted(async () => {
      try {
        await store.dispatch('shop/fetchAlgoliaProducts', {
          productIds: props.products.map((id) => decodeURI(id)),
          nbItemsToFetch: props.products.length,
        })
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error(err)
      }
      const products = store.getters['shop/getAlgoliaProductsByIds'](props.products)
      // @ts-ignore: approfondir pourquoi le match ne se fait pas
      displayedProducts.value = isAuthenticated.value ? products.filter((product) => !!product.bestOffer) : products
      if (displayedProducts.value?.length) editorialsReactions.pushProductsAnchorMounted(props.anchorLabel)
      context.emit('carousel-products-loaded', displayedProducts.value?.length)
    })

    function onCtaClicked() {
      const payload = { content_title: props.title }
      useAnalyticService().trackEvent(props.ctaClickEventName, payload)
    }

    function getVisibileItemNames(itemListIdx) {
      const content_title = itemListIdx?.map((idx) => props.products[idx]) ?? []
      return content_title
    }

    function isSameList(prevList, nextList) {
      return prevList.length === nextList.length && prevList.every((item) => nextList.includes(item))
    }

    function visibilityChanged(isVisible) {
      if (isVisible) {
        const currentVisible = getVisibileItemNames(theCarousel.value?.visibleItemsIdx)
        const okToSend = !isSameList(currentDisplayed, currentVisible)
        currentDisplayed = currentVisible
        if (okToSend) {
          const payLoad = {
            content_title: currentVisible,
          }
          useAnalyticService().trackEvent('view_product_thumbnail', payLoad)
        }
      }
    }

    function carouselVisibilityChanged(event) {
      carouselDisplayed = true
      visibilityChanged(event)
    }

    function itemsVisibilityChanged() {
      if (carouselDisplayed && theCarousel.value?.visibleItemsIdx?.length) visibilityChanged(true)
    }

    function onLogoClicked() {
      context.emit('logo-clicked')
    }

    return {
      displayedProducts,
      isAuthenticated,
      onCtaClicked,
      onLogoClicked,
      carouselVisibilityChanged,
      itemsVisibilityChanged,
      visibilityChanged,
      theCarousel,
    }
  },
})
</script>

<style lang="scss">
.carousel-products {
  --idf-carousel-items-gap: 0.5rem;

  &.idf-carousel--allItemsVisible {
    --idf-carousel-items-justify-content: center;
  }

  li {
    width: 25rem;
  }

  &__logo {
    display: flex;
    justify-content: center;

    @media (min-width: $breakpoint-lg) {
      justify-content: flex-start;
    }

    img {
      height: 4.4rem;
      max-width: 100%;
      object-fit: contain;
    }
  }

  &__footer {
    display: flex;
    justify-content: center;
  }
}
</style>
